import React, { useState } from "react";
import { Base } from "../base";
import { Field, Form, Formik } from "formik";
import { AutocompleteChangeReason, Box, Grid } from "@mui/material";
import { useSelector } from "../../store";
import { Inputs } from "../formInputs";
import * as Yup from "yup";
import SearchParams, { defaultParams } from "../../types/searchParams";
import { debounce } from "lodash";
import theme from "../../theme";

interface Props {
  apiSiteManagerSearch: (search: SearchParams) => void;
  apiSiteTypeSearch: (search: SearchParams) => void;
  apiStateSearch: (search: SearchParams) => void;
  submitSite: (values: any) => void;
  resetSite: () => void;
  handleCancel: () => void;
}

const AddEditSiteForm: React.FC<Props> = (props) => {
  const {
    apiSiteManagerSearch,
    apiSiteTypeSearch,
    apiStateSearch,
    submitSite,
    handleCancel,
  } = props;

  const { site, status } = useSelector((store) => store.site);
  const { siteManagerSearchResults } = useSelector(
    (store) => store.siteManager
  );
  const { siteTypeSearchResults } = useSelector((store) => store.siteType);
  const { status: fileStatus } = useSelector((store) => store.attachments);
  const [siteManagerFilter, setSiteManagerFilter] =
    useState<SearchParams>(defaultParams);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Required"),
    siteNumber: Yup.string().nullable(),
    siteManager: Yup.object().nullable(),
    siteType: Yup.object().required("Required").typeError("Selection Required"),
    address: Yup.object()
      .shape({
        line1: Yup.string().nullable(),
        line2: Yup.string().nullable(),
        city: Yup.string().nullable(),
        usaState: Yup.object().nullable(),
        zipCode: Yup.string().nullable().min(5, "Enter a valid zip code"),
      })
      .nullable(),
    contactName: Yup.string().required("Required").typeError("Required"),
    contactPhone: Yup.string().required("Required").typeError("Required"),
    contactEmail: Yup.string().required("Required").typeError("Required"),
  });

  const updateFilterSearch = debounce(
    (query: string, type: "manager" | "site-type" | "state") => {
      switch (type) {
        case "manager":
          apiSiteManagerSearch({
            ...defaultParams,
            query: query,
          });
          break;
        case "site-type":
          apiSiteTypeSearch({
            ...defaultParams,
            query: query,
          });
          break;
        case "state":
          apiStateSearch({
            ...defaultParams,
            query: query,
          });
          break;

        default:
          break;
      }
    },
    500
  );

  return (
    <Box
      sx={{
        padding: 5,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
      }}
    >
      <Formik
        initialValues={{
          ...site,
          address: {
            id: site.address?.id ?? 0,
            line1: site.address?.line1,
            line2: site.address?.line2,
            city: site.address?.city,
            usaState:
              site.address?.usaState?.id === null
                ? undefined
                : site.address?.usaState,
            zipCode: site.address?.zipCode,
            placeId: site.address?.placeId,
          },
        }}
        onSubmit={submitSite}
        validationSchema={validationSchema}
        enableReinitialize
      >
        <Form noValidate>
          <Grid container direction={"row"} pb={5} columnSpacing={2}>
            <Grid size={{ md: 6, xs: 12 }}>
              <Field
                name="name"
                label="Site Name"
                component={Inputs.Text}
                required
              />
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <Field
                name="siteNumber"
                label="Site Number"
                component={Inputs.Text}
              />
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <Field
                name="siteManager"
                label="Site Manager"
                component={Inputs.Dropdown}
                options={
                  siteManagerSearchResults?.results && siteManagerFilter.query
                    ? siteManagerSearchResults?.results
                    : []
                }
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  updateFilterSearch(e.currentTarget.value, "manager");
                  setSiteManagerFilter({
                    ...siteManagerFilter,
                    query: e.currentTarget.value,
                  });
                }}
                noOptionsText={
                  siteManagerFilter.query === ""
                    ? "Start typing to search"
                    : undefined
                }
                // required
                onSelected={(
                  e: React.SyntheticEvent,
                  value: any,
                  reason: AutocompleteChangeReason
                ) => {
                  if (reason === "clear") {
                    apiSiteManagerSearch(defaultParams);
                  }
                }}
              />
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <Field
                name="siteType"
                label="Site Type"
                component={Inputs.Dropdown}
                options={siteTypeSearchResults?.results || []}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  updateFilterSearch(e.currentTarget.value, "site-type");
                }}
                onFocus={() => {
                  apiSiteTypeSearch(defaultParams);
                }}
                required
                onSelected={(
                  e: React.SyntheticEvent,
                  value: any,
                  reason: AutocompleteChangeReason
                ) => {
                  if (reason === "clear") {
                    apiSiteTypeSearch(defaultParams);
                  }
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Field
                name="addressSearch"
                label="Address"
                component={Inputs.Address}
                fieldName="address"
                placeId={site.address?.placeId}
              />
            </Grid>
            {/* Test hide fields and use search instead */}
            {/* <Grid size={{ md: 4, xs: 12 }}>
              <Field
                name="address.line1"
                label="Line 1"
                component={Inputs.Text}
                disabled
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <Field
                name="address.line2"
                label="Line 2"
                component={Inputs.Text}
                disabled
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <Field
                name="address.city"
                label="City"
                component={Inputs.Text}
                disabled
              />
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <Field
                name="address.usaState"
                label="State/Province/Territory"
                component={Inputs.Dropdown}
                options={statesSearchResults?.results || []}
                disabled
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  updateFilterSearch(e.currentTarget.value, "state");
                }}
                onFocus={() => {
                  apiStateSearch(defaultParams);
                }}
                onSelected={(
                  e: React.SyntheticEvent,
                  value: any,
                  reason: AutocompleteChangeReason
                ) => {
                  if (reason === "clear") {
                    apiStateSearch(defaultParams);
                  }
                }}
              />
            </Grid>
            <Grid size={{ md: 6, xs: 12 }}>
              <Field
                name="address.zipCode"
                label="Postal/Province/Territory Code"
                component={Inputs.Text}
                disabled
              />
            </Grid> */}
            <Grid size={{ md: 4, xs: 12 }}>
              <Field
                name="contactName"
                label="Site Contact Name"
                component={Inputs.Text}
                required
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <Field
                name="contactPhone"
                label="Site Contact Phone"
                component={Inputs.Text}
                required
              />
            </Grid>
            <Grid size={{ md: 4, xs: 12 }}>
              <Field
                name="contactEmail"
                label="Site Contact Email"
                component={Inputs.Text}
                required
              />
            </Grid>
          </Grid>
          <Grid
            container
            direction={"row"}
            justifyContent={"space-around"}
            sx={{
              backgroundColor: "white",
              // paddingY: 2,
              zIndex: 1000,
            }}
          >
            <Grid>
              <Base.Button
                text="Cancel"
                type="secondary"
                onClick={handleCancel}
              />
            </Grid>
            <Grid>
              <Inputs.Submit
                isSubmitting={status === "loading" || fileStatus === "loading"}
                text="Save"
              />
            </Grid>
          </Grid>
        </Form>
      </Formik>
    </Box>
  );
};

export default AddEditSiteForm;
